import FetchOrganizationWorkflows from '@Application/Organizations/Workflows/Queries/FetchOrganizationWorkflows';
import fetchOrganizationWorkflows from '@Application/Organizations/Workflows/fetchOrganizationWorkflows';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import type OrganizationWorkflowsView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowsView';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type UseOrganizationWorkflowsResult = {
	/** Null until loaded for the current organization. */
	workflows: OrganizationWorkflowsView | null;
	error: Error | null;
	refetch: () => void;
	organizationId: number | null;
	/** True once the current organization is known to be absent rather than still being resolved. */
	hasNoOrganization: boolean;
};

/**
 * Workflows of the current organization (the header selector's own choice). Without an organization
 * `query` never leaves, so `stopOn` reports "done" at once; the caller tells "none" from "still
 * resolving" through `hasNoOrganization`.
 */
function useOrganizationWorkflows(): UseOrganizationWorkflowsResult {
	const dispatch = useDispatch();
	const { currentOrganization, isResolved } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.organizationWorkflowsStore.workflows,
		stopOn: (value) => organizationId === null || (!!value && value.organizationId === organizationId),
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchOrganizationWorkflows(new FetchOrganizationWorkflows(organizationId)));
		},
		deps: [organizationId],
	});

	return {
		workflows: data !== null && data.organizationId === organizationId ? data : null,
		error,
		refetch,
		organizationId,
		hasNoOrganization: organizationId === null && isResolved,
	};
}

export default useOrganizationWorkflows;
